import { Link, useLocation, useParams } from 'react-router-dom';
import { useMemo } from 'react';
import { styled, useTheme, Theme } from '@mui/material/styles';
import { Box, Breadcrumbs as MuiBreadcrumbs, Typography } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';
import MainCard from 'components/MainCard';
import { welcomePagePath } from 'routes/paths';

const StyledLink = styled(Link)(({ theme }) => ({
    textDecoration: 'none',
    color: theme.palette.text.secondary,
    fontWeight: 500,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.spacing(0.75),
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
        color: theme.palette.primary.main,
    },
    '&:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2,
    },
}));

const labelMap: Record<string, string> = {
    '': 'Home',
    workspace: 'Workspace',
    data: 'Data',
    workflows: 'Workflows',
    datasets: 'Datasets',
    'upload-datasets': 'Upload Datasets',
    'workflow-datasets': 'Workflow DataSets',
    models: 'Models',
    about: 'About',
    profile: 'Profile',
    uploadDataSet: 'Upload Dataset',
    uploadDataset: 'Upload Dataset',
    details: 'Details',
    users: 'Users',
    resources: 'Resources',
    manageTags: 'Manage Tags',
    accessRequests: 'Access Requests',
    workflowCreate: 'Create Workflow',
    executions: 'Executions',
    fileList: 'File List',
    edit: 'Edit',
    workflowDataSets: 'Workflow DataSets',
    update: 'Update Dataset',
    logs: 'Logs',
    inference: 'Inference',
};

const formatSegment = (segment: string): string =>
    labelMap[segment] ??
    decodeURIComponent(segment).charAt(0).toUpperCase() + decodeURIComponent(segment).slice(1).replace(/-/g, ' ');

const Breadcrumbs = () => {
    const theme = useTheme();
    const location = useLocation();
    const params = useParams();

    const paramValues = useMemo(() => Object.values(params).filter(Boolean) as string[], [params]);

    const segments = useMemo(() => location.pathname.split('/').filter(Boolean), [location.pathname]);

    const crumbs = useMemo(() => {
        const items: { label: string; href?: string; key: string }[] = [];
        let pathAcc = '';

        segments.forEach((seg, idx) => {
            pathAcc += `/${seg}`;
            const isLast = idx === segments.length - 1;
            const label = formatSegment(seg);

            items.push({
                label,
                href: isLast ? undefined : pathAcc,
                key: seg || String(idx),
            });
        });

        return items;
    }, [segments]);

    const isHomePage = location.pathname === '/' || location.pathname === `/${welcomePagePath()}`;

    if (segments.length === 0 || isHomePage) {
        return null;
    }

    return (
        <MainCard
            sx={{
                mb: 1,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
            }}
            shadow='none'
            contentSX={{
                px: 2,
                py: 1.25,
                '&.MuiCardContent-root': {
                    paddingTop: '10px !important',
                    paddingBottom: '10px !important',
                },
            }}>
            <MuiBreadcrumbs
                aria-label='breadcrumb'
                separator={
                    <NavigateNextIcon
                        sx={{
                            fontSize: 20,
                            color: theme.palette.text.disabled,
                        }}
                    />
                }
                sx={{
                    '& .MuiBreadcrumbs-ol': {
                        flexWrap: 'nowrap',
                    },
                    '& .MuiBreadcrumbs-li': {
                        display: 'flex',
                        alignItems: 'center',
                    },
                }}>
                <StyledLink to={`/${welcomePagePath()}`}>
                    <HomeIcon sx={{ fontSize: 18 }} />
                    <Typography variant='body2' sx={{ fontWeight: 500 }}>
                        Home
                    </Typography>
                </StyledLink>

                {crumbs.map((crumb, idx) => {
                    const isLast = idx === crumbs.length - 1;
                    const isDynamicParam = paramValues.includes(decodeURIComponent(crumb.key));
                    const isStaticCrumb = isLast || isDynamicParam;

                    if (isStaticCrumb) {
                        return (
                            <Box
                                key={crumb.key}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    px: 1,
                                    py: 0.5,
                                }}>
                                <Typography
                                    variant='body2'
                                    sx={{
                                        fontWeight: 600,
                                        color: theme.palette.text.primary,
                                        cursor: 'default',
                                    }}>
                                    {crumb.label}
                                </Typography>
                            </Box>
                        );
                    }

                    return (
                        <StyledLink to={crumb.href || `/${welcomePagePath()}`} key={crumb.key}>
                            <Typography variant='body2' sx={{ fontWeight: 500 }}>
                                {crumb.label}
                            </Typography>
                        </StyledLink>
                    );
                })}
            </MuiBreadcrumbs>
        </MainCard>
    );
};

export default Breadcrumbs;
